import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import TradeFilters from './components/TradeFilters';
import TradeTable from './components/TradeTable';
import BulkActionToolbar from './components/BulkActionToolbar';
import ConnectionStatus from './components/ConnectionStatus';
import TradeMetrics from './components/TradeMetrics';
import LivePriceCharts from './components/LivePriceCharts';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { astraAPI } from '../../services/astraApi';
import { SkeletonCard, SkeletonList } from '../../components/ui/Skeleton';
import useAutoRefresh from '../../hooks/useAutoRefresh';

/**
 * Live Trading Feed
 *
 * 🔧 PRODUCTION VERSION (2025-12-28): Enhanced with 2025 UI Best Practices
 *
 * Enhancements:
 * - Framer Motion animations for smooth transitions
 * - useAutoRefresh hook for butter-smooth data refresh
 * - Skeleton loading states
 * - Glass-card hover effects
 * - Live pulse animations
 */

const LiveTradingFeed = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [selectedTrades, setSelectedTrades] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Auto-refresh using custom hook (every 5 seconds for live trading feed)
  const { refreshCount } = useAutoRefresh({
    intervalMs: 5000,
    enabled: true,
    onRefresh: () => {
      fetchTradeData();
      fetchConnectionStatus();
    }
  });

  // Initial data fetch
  useEffect(() => {
    fetchTradeData();
    fetchConnectionStatus();
  }, []);

  const fetchConnectionStatus = async () => {
    try {
      // Fetch real API health data
      const apiHealth = await astraAPI.getAPIServicesHealth();

      if (apiHealth && apiHealth.services) {
        const connectionsList = Object.entries(apiHealth.services).map(([name, data], index) => ({
          id: index + 1,
          name: name,
          endpoint: data.endpoint || 'N/A',
          status: data.status === 'healthy' || data.status === 'connected' ? 'connected' : 'disconnected',
          latency: data.latency || data.response_time || 0,
          uptime: data.uptime ? `${(data.uptime * 100).toFixed(1)}%` : 'N/A',
          requests: data.request_count || data.calls || 0
        }));
        setConnections(connectionsList);
      }
    } catch (err) {
      console.error('Failed to fetch connection status:', err);
      // Show minimal status on error
      setConnections([{
        id: 1,
        name: 'API Health Check Failed',
        endpoint: 'Unable to connect',
        status: 'disconnected',
        latency: 0,
        uptime: 'N/A',
        requests: 0
      }]);
    }
  };

  const fetchTradeData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to get trade history first
      // 🔧 FIX (2025-12-29): Increased limit from 50 to 500 to show all recent trades
      let formattedTrades = [];
      try {
        const tradeHistory = await astraAPI.getTradeHistory(1453, 500);
        // 🔧 FIX: API returns "items", not "trades"
        const trades = tradeHistory.items || tradeHistory.trades || [];
        if (trades.length > 0) {
          formattedTrades = trades.map(trade => ({
            id: trade.id,
            // 🔧 FIX: Map API fields correctly (entry_time -> timestamp, side -> action)
            timestamp: new Date(trade.entry_time || trade.timestamp || trade.created_at),
            entry_time: trade.entry_time,  // 🔧 ADD: For execution time calculation
            exit_time: trade.exit_time,    // 🔧 ADD: For execution time calculation
            symbol: trade.symbol,
            assetType: trade.asset_class || 'unknown',
            action: trade.side || trade.action || 'UNKNOWN',
            quantity: trade.quantity,
            price: trade.price,
            currency: trade.currency || 'INR',  // 🔧 ADD: Track currency for volume
            amount: (trade.quantity * trade.price),
            status: trade.status || 'completed',
            aiConfidence: trade.confidence || 0.5,
            strategy: trade.strategy || 'Unknown',
            executionLatency: trade.execution_time_ms || 0,
            isNew: false
          }));
        }
      } catch (tradeErr) {
        console.warn('Trade history not available, using positions data:', tradeErr.message);

        // Fallback to recent orders if trade history unavailable
        try {
          const ordersData = await astraAPI.getTradingOrders('all', 100, 1453);
          if (ordersData && ordersData.length > 0) {
            formattedTrades = ordersData.map(order => ({
              id: order.id,
              timestamp: new Date(order.timestamp),
              symbol: order.symbol,
              assetType: order.asset_class || 'unknown',
              action: order.side,
              quantity: order.quantity,
              price: order.price,
              amount: order.value || (order.quantity * order.price),
              status: order.status,
              aiConfidence: order.confidence || 0.5,
              strategy: order.strategy || 'unknown',
              executionLatency: order.execution_time_ms || 0,
              isNew: false
            }));
          }
        } catch (orderErr) {
          console.error('Orders data not available:', orderErr);
        }
      }

      if (formattedTrades.length > 0) {
        setTrades(formattedTrades);
      } else {
        // No data available - show empty state
        setTrades([]);
        setError('No trading data available. Start trading to see live feed.');
      }
    } catch (err) {
      console.error('Failed to fetch trade data:', err);
      setError('Failed to load trade data from server');
      setTrades([]);
    } finally {
      setLoading(false);
    }
  };

  // Real metrics data from API
  const [metrics, setMetrics] = useState({
    totalTrades: 0,
    tradesChange: 0,
    executedTrades: 0,
    executedChange: 0,
    pendingTrades: 0,
    pendingChange: 0,
    failedTrades: 0,
    failedChange: 0,
    totalVolume: 0,
    volumeChange: 0,
    avgExecutionTime: 0,
    executionTimeChange: 0
  });

  // Update metrics when trades change
  useEffect(() => {
    if (trades.length > 0) {
      // 🔧 PRODUCTION FIX (2025-10-31): Filter today's trades only
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const todayTrades = trades.filter(t => {
        const tradeDate = new Date(t.timestamp || t.entry_time || t.created_at);
        tradeDate.setHours(0, 0, 0, 0);
        return tradeDate.getTime() === today.getTime();
      });

      // 🔧 FIX (2025-10-30): Properly check status (API returns lowercase)
      const executedTrades = todayTrades.filter(t =>
        t.status && (t.status.toLowerCase() === 'executed' || t.status.toLowerCase() === 'completed')
      ).length;
      const pendingTrades = trades.filter(t =>
        t.status && t.status.toLowerCase() === 'pending'
      ).length;
      const failedTrades = trades.filter(t =>
        t.status && t.status.toLowerCase() === 'failed'
      ).length;

      // 🔧 FIX (2025-10-30): Calculate volume with proper currency conversion
      // Convert USDT trades to INR for consistent totals
      const exchangeRate = 88.53; // Should fetch from API ideally
      const totalVolume = todayTrades.reduce((sum, t) => {
        const amount = t.amount || (t.quantity * t.price) || 0;
        // Convert USDT to INR for volume total
        const amountInINR = t.currency === 'USDT' ? amount * exchangeRate : amount;
        return sum + amountInINR;
      }, 0);

      // 🔧 FIX (2025-10-30): Calculate execution time from entry_time to exit_time
      const tradesWithTime = todayTrades.filter(t => t.entry_time && t.exit_time);
      let avgExecutionTime = 0;
      if (tradesWithTime.length > 0) {
        const totalTime = tradesWithTime.reduce((sum, t) => {
          const entryTime = new Date(t.entry_time);
          const exitTime = new Date(t.exit_time);
          const timeDiff = exitTime - entryTime; // milliseconds
          return sum + timeDiff;
        }, 0);
        avgExecutionTime = Math.round(totalTime / tradesWithTime.length);
      }

      setMetrics({
        totalTrades: todayTrades.length, // 🔧 FIX: Only today's trades
        tradesChange: 0, // Would need historical data to calculate
        executedTrades,
        executedChange: 0,
        pendingTrades,
        pendingChange: 0,
        failedTrades,
        failedChange: 0,
        totalVolume,
        volumeChange: 0,
        avgExecutionTime,
        executionTimeChange: 0
      });
    } else {
      // Reset metrics if no trades
      setMetrics({
        totalTrades: 0,
        tradesChange: 0,
        executedTrades: 0,
        executedChange: 0,
        pendingTrades: 0,
        pendingChange: 0,
        failedTrades: 0,
        failedChange: 0,
        totalVolume: 0,
        volumeChange: 0,
        avgExecutionTime: 0,
        executionTimeChange: 0
      });
    }
  }, [trades]);

  // Real connection status data from API
  const [connections, setConnections] = useState([]);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleTradeSelect = (tradeId, isSelected) => {
    setSelectedTrades(prev =>
      isSelected
        ? [...prev, tradeId]
        : prev?.filter(id => id !== tradeId)
    );
  };

  const handleSelectAll = (isSelected) => {
    setSelectedTrades(isSelected ? trades?.map(trade => trade?.id) : []);
  };

  const handleBulkAction = (action) => {
    console.log(`Performing bulk action: ${action} on trades:`, selectedTrades);
    // Implement bulk action logic here
    setSelectedTrades([]);
  };

  const handleClearSelection = () => {
    setSelectedTrades([]);
  };

  const handleApplyFilters = (filters) => {
    console.log('Applying filters:', filters);
    // Implement filter logic here
  };

  const handleResetFilters = () => {
    console.log('Resetting filters');
    // Implement reset logic here
  };

  const handleRefresh = () => {
    console.log('Refreshing trade data');
    // Implement refresh logic here
  };

  const handleExport = (format) => {
    console.log(`Exporting data as ${format}`);
    // Implement export logic here
  };

  // Loading state with skeleton
  if (loading && trades.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />
        <main className={`transition-all duration-300 ${
          isSidebarCollapsed ? 'ml-16' : 'ml-64'
        } pt-16`}>
          <div className="p-6">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6">
              <h1 className="text-2xl font-bold text-foreground">Live Trading Feed</h1>
              <p className="text-muted-foreground mt-1">Loading real-time trading data...</p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <SkeletonCard className="h-24" />
              <SkeletonCard className="h-24" />
              <SkeletonCard className="h-24" />
              <SkeletonCard className="h-24" />
            </div>
            <SkeletonCard className="h-96" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <main className={`transition-all duration-300 ${
        isSidebarCollapsed ? 'ml-16' : 'ml-64'
      } pt-16 ${isFiltersVisible ? 'mr-80' : 'mr-0'}`}>
        <div className="p-6">
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex items-center justify-between mb-6"
          >
            <div>
              <Breadcrumb />
              <div className="flex items-center space-x-4">
                <h1 className="text-3xl font-bold text-foreground">Live Trading Feed</h1>
                <div className="flex items-center space-x-2 px-3 py-1 bg-success/10 text-success rounded-full">
                  <div className="w-2 h-2 bg-success rounded-full animate-live-pulse"></div>
                  <span className="text-sm font-medium">Live</span>
                </div>
              </div>
              <p className="text-muted-foreground mt-2">
                Real-time monitoring of all trade executions and pending orders
              </p>
            </div>

            <div className="flex items-center space-x-3">
              <div className="text-right">
                <div className="text-sm font-medium text-foreground">
                  {currentTime?.toLocaleTimeString('en-IN')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {currentTime?.toLocaleDateString('en-IN')}
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => setIsFiltersVisible(!isFiltersVisible)}
                iconName="Filter"
                iconPosition="left"
                iconSize={16}
              >
                Filters
              </Button>
            </div>
          </motion.div>

          {/* Metrics Overview */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <TradeMetrics metrics={metrics} refreshTrigger={refreshCount} />
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.15 }}
          >
            <QuickActionToolbar
              selectedCount={selectedTrades?.length}
              onRefresh={handleRefresh}
              onExport={handleExport}
              onFilter={() => setIsFiltersVisible(!isFiltersVisible)}
              className="mb-6"
            />
          </motion.div>

          {/* Live Price Charts Section */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.18 }}
            className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6"
          >
            <div className="xl:col-span-2">
              <LivePriceCharts refreshTrigger={refreshCount} />
            </div>
            <div>
              <ConnectionStatus connections={connections} refreshTrigger={refreshCount} />
            </div>
          </motion.div>

          {/* Main Content Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="grid grid-cols-1 gap-6"
          >
            {/* Trade Table - Full width now */}
            <div>
              <div className="bg-card border border-border rounded-lg glass-card-hover">
                <TradeTable
                  trades={trades}
                  selectedTrades={selectedTrades}
                  onTradeSelect={handleTradeSelect}
                  onSelectAll={handleSelectAll}
                  onBulkAction={handleBulkAction}
                  refreshTrigger={refreshCount}
                />
              </div>
            </div>
          </motion.div>

          {/* Real-time Updates Info */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
            className="mt-6 p-4 bg-muted/50 rounded-lg border border-border glass-card-hover"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon name="Activity" size={20} className="text-primary" />
                <div>
                  <div className="text-sm font-medium text-foreground">
                    Real-time Data Stream Active
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Last update: {new Date()?.toLocaleTimeString('en-IN')} • Refresh #{refreshCount}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-success rounded-full animate-live-pulse"></div>
                  <span>WebSocket Connected</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-success rounded-full animate-live-pulse"></div>
                  <span>Database Synced</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
      {/* Filter Sidebar */}
      <TradeFilters
        isVisible={isFiltersVisible}
        onToggle={() => setIsFiltersVisible(!isFiltersVisible)}
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
      />
      {/* Bulk Action Toolbar */}
      <BulkActionToolbar
        selectedCount={selectedTrades?.length}
        onBulkAction={handleBulkAction}
        onClearSelection={handleClearSelection}
      />
    </div>
  );
};

export default LiveTradingFeed;